import { computed, Directive, ElementRef, HostBinding, HostListener, inject, Input } from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';

import { DatePickerRange } from '@treetop/shared/model';

import { DatepickerControl } from './datepicker-panel.directive';
import { CustomDateAdapter, DatepickerComponent } from './datepicker.component';
import { FormFieldControl } from '../form-field/form-field-control';
import { ErrorStateMatcher } from '../forms/error-options';
import { ErrorStateTracker } from '../forms/error-state-tracker';

@Directive({
  selector: 'input[ttDatepickerRange]',
  standalone: true,
  providers: [
    { provide: FormFieldControl, useExisting: DatepickerRangeInputDirective },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
})
export class DatepickerRangeInputDirective implements FormFieldControl, DatepickerControl, ControlValueAccessor {
  protected _elementRef = inject(ElementRef);

  public ngControl = inject(NgControl, { optional: true, self: true });
  private defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private parentFormGroup = inject(FormGroupDirective, { optional: true });
  private parentForm = inject(NgForm, { optional: true });
  private _dateAdapter = inject(DateAdapter);
  private datePicker: DatepickerComponent;

  errorState = computed(() => this.errorStateTracker.errorState());
  private errorStateTracker: ErrorStateTracker;
  private disabled = false;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onChange: (value: DatePickerRange | null) => void = () => {};

  public getCurrentValue(): DatePickerRange | null {
    return this.ngControl?.control?.value ?? null;
  }

  @Input() set ttDatepickerRange(datepicker: DatepickerComponent) {
    this.datePicker = datepicker;
    datepicker.registerInput(this);
    datepicker.selectedRangeChange.subscribe((range) => {
      this._onChange(range);
      this._onTouched();
      this._updateDisplayValue(range);
    });
  }

  @HostBinding('class.tt-datepicker-input') ttDatepickerInput = true;

  @HostBinding('class.tt-datepicker-input--disabled') get isDisabledClass(): boolean {
    return this.disabled;
  }

  @HostListener('click') handleClick(): void {
    this.datePicker?.open();
  }

  constructor() {
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
    this.errorStateTracker = new ErrorStateTracker(this.defaultErrorStateMatcher, this.ngControl, this.parentFormGroup, this.parentForm);
  }

  public getConnectedOverlayOrigin(): ElementRef {
    return this._elementRef;
  }

  public isDisabled(): boolean {
    return this.disabled;
  }

  writeValue(value: DatePickerRange | null): void {
    this._updateDisplayValue(value);
  }

  registerOnChange(fn: (value: DatePickerRange | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private _updateDisplayValue(range: DatePickerRange | null): void {
    if (!range) {
      this._elementRef.nativeElement.value = '';
      return;
    }

    const start = range.start ? this._dateAdapter.format(range.start, undefined) : '';
    const end = range.end ? this._dateAdapter.format(range.end, undefined) : '';
    this._elementRef.nativeElement.value = start && end ? `${start} – ${end}` : start || end;
  }
}
